import { useMemo } from 'react';
import { Switch } from 'antd';

import AxisConfigDropdown from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import { colorConstants } from 'config/colorConstants';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';

import { getKpiBinLabelAffixesForXAxis, getXAxisKpiOptionIds } from './packArchitectureMeta.helpers';

/**
 * Reads all X-axis settings from context directly — no prop drilling needed.
 * Kept private to this module; only used by PackArchitectureToolbar.
 */
const XAxisConfigDropdown = () => {
    const {
        xAxis,
        setXAxis,
        xAxisConfig,
        xAxisOptionGroups,
        xAxisGroupByControlsMeta,
        xAxisKpiControlsMeta,
        xAxisKpiDataRange,
        xAxisDimensionConfig,
        setXAxisDimensionConfig,
        xAxisKpiConfig,
        setXAxisKpiConfig,
        chartRequest,
        totalCount,
    } = usePackArchitectureContext();

    const currencyPrefix = useGetCurrencyForTenant();

    const kpiBinLabelAffixes = useMemo(
        () => getKpiBinLabelAffixesForXAxis(xAxis, xAxisConfig, currencyPrefix),
        [currencyPrefix, xAxis, xAxisConfig],
    );

    const xAxisKpiOptionIds = useMemo(() => getXAxisKpiOptionIds(xAxisConfig), [xAxisConfig]);

    return (
        <AxisConfigDropdown
            label={xAxisConfig?.label ?? 'X-Axis'}
            value={xAxis}
            onChange={setXAxis}
            optionGroups={xAxisOptionGroups}
            kpiColumnOptionIds={xAxisKpiOptionIds}
            showSettings
            groupByControlsMeta={xAxisGroupByControlsMeta}
            kpiControlsMeta={xAxisKpiControlsMeta}
            chartRequest={chartRequest}
            apiTotalCount={totalCount}
            kpiDataRange={xAxisKpiDataRange}
            kpiBinLabelAffixes={kpiBinLabelAffixes}
            axisConfiguration={xAxisDimensionConfig}
            onAxisConfigurationChange={setXAxisDimensionConfig}
            kpiAxisConfiguration={xAxisKpiConfig}
            onKpiAxisConfigurationChange={setXAxisKpiConfig}
        />
    );
};

const PackArchitectureToolbar = () => {
    const {
        yAxis,
        setYAxis,
        yAxisConfig,
        yAxisOptionGroups,
        bubbleRepresents,
        setBubbleRepresents,
        bubbleRepresentsConfig,
        bubbleRepresentsOptionGroups,
        bubbleSize,
        setBubbleSize,
        bubbleSizeConfig,
        bubbleSizeOptionGroups,
        showHeatMap,
        setShowHeatMap,
        heatMapToggle,
    } = usePackArchitectureContext();

    const label = useMemo(() => {
        if(!bubbleSizeOptionGroups || bubbleSizeOptionGroups?.length === 0 || !bubbleSizeOptionGroups[0].options) return '';
        return bubbleSizeOptionGroups[0].options?.find((option: any) => option.value === bubbleSize)?.label || '';
    }, [bubbleSize, bubbleSizeOptionGroups]);

    return (
        <div className="mb-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4">
                <XAxisConfigDropdown />
                <AxisConfigDropdown
                    label={yAxisConfig?.label ?? 'Y-Axis'}
                    value={yAxis}
                    onChange={setYAxis}
                    optionGroups={yAxisOptionGroups}
                />
            </div>

            <div className="h-8 w-px shrink-0 bg-borderGreyAl1" role="separator" aria-orientation="vertical" />

            <div
                className="pack-architecture-combined-bubble-config inline-flex h-8 items-stretch overflow-hidden rounded-md border border-borderGreyAl1 bg-white"
            >
                <AxisConfigDropdown
                    embedded
                    label={bubbleRepresentsConfig?.label ?? 'Bubble Represents'}
                    value={bubbleRepresents}
                    onChange={setBubbleRepresents}
                    optionGroups={bubbleRepresentsOptionGroups}
                />
                <div className="w-px shrink-0 self-stretch bg-borderGreyAl1" role="separator" aria-orientation="vertical" />
                <AxisConfigDropdown
                    embedded
                    label={bubbleSizeConfig?.label ?? 'Size'}
                    value={bubbleSize}
                    onChange={setBubbleSize}
                    optionGroups={bubbleSizeOptionGroups}
                />
            </div>

            {heatMapToggle ? (
                <div
                    className="ml-auto flex items-center gap-2"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Switch size="small" checked={showHeatMap} onChange={setShowHeatMap} />
                    <span className="text-[14px]" style={{ color: colorConstants.blackTransparent88 }}>
                        {heatMapToggle.label} : {label}
                    </span>
                </div>
            ) : null}
        </div>
    );
};

export default PackArchitectureToolbar;
